/**
 * 水印效果
 * 用于创建和管理页面水印效果
 */
class Watermark {
    constructor(options = {}) {
        // 默认配置
        this.config = {
            text: '齐心<br>Comix',
            rows: 10,
            cols: 20,
            opacity: 0.2,
            fontSize: '14px',
            color: '#333',
            rotate: -20,
            zIndex: 9999,
            enableProtection: true,    // 默认启用防篡改保护
            ...options
        };
        
        this.watermarkId = 'app-watermark-container';
        this.styleId = 'app-watermark-styles';
        this.watermarkElement = null;
        this.observer = null;  // 跟踪MutationObserver实例
    }

    /**
     * 创建水印样式
     */
    createStyles() {
        // 如果样式已存在则不重复创建
        if (document.getElementById(this.styleId)) {
            return;
        }

        const style = document.createElement('style');
        style.id = this.styleId;
        style.innerHTML = `
            .watermark-container {
                width: 100vw;
                height: 100vh;
                pointer-events: none;
                position: fixed;
                top: 0;
                left: 0;
                z-index: ${this.config.zIndex};
                overflow: hidden;
            }
            
            .watermark-content {
                width: 130%;
                height: 130%;
                transform: translate(-15%, -15%);
            }
            
            .watermark-box {
                width: 10000px;
                display: flex;
                justify-content: center;
                position: relative;
                left: 50%;
                transform: translateX(-50%);
                margin: 90px 0;
            }
            
            .watermark-item {
                display: flex;
                align-items: center;
                justify-content: center;
                margin: 0 60px;
            }
            
            .watermark-text {
                transform: rotate(${this.config.rotate}deg);
                opacity: ${this.config.opacity};
                font-size: ${this.config.fontSize};
                color: ${this.config.color};
                text-align: left;
                line-height: 1.2;
                user-select: none;
                white-space: nowrap;
                font-family: "Microsoft YaHei", sans-serif;
            }
            
            .watermark-text.watermark-opacity {
                opacity: 0;
            }
        `;
        
        document.head.appendChild(style);
    }

    /**
     * 创建水印HTML结构
     */
    createWatermarkHTML() {
        let html = '<div class="watermark-content">';
        
        for (let i = 1; i <= this.config.rows; i++) {
            html += `<div class="watermark-box">`;
            
            for (let j = 1; j <= this.config.cols; j++) {
                const isOdd = i % 2 === 1;
                const opacityClass = (isOdd ? j % 2 === 1 : j % 2 === 0) ? 'watermark-opacity' : '';
                
                html += `
                    <div class="watermark-item">
                        <div class="watermark-text ${opacityClass}">
                        <div>${this.config.text}</div>
                        </div>
                    </div>
                `;
            }
            
            html += '</div>';
        }
        
        html += '</div>';
        return html;
    }

    /**
     * 初始化水印
     */
    init() {
        try {
            // 创建样式
            this.createStyles();
            
            // 移除已存在的水印
            this.destroy();
            
            // 创建水印容器
            const watermarkContainer = document.createElement('div');
            watermarkContainer.id = this.watermarkId;
            watermarkContainer.className = 'watermark-container';
            watermarkContainer.innerHTML = this.createWatermarkHTML();
            
            // 添加到body
            document.body.appendChild(watermarkContainer);
            this.watermarkElement = watermarkContainer;
            
            // 根据配置自动启用防篡改保护
            if (this.config.enableProtection) {
                this.enableProtection();
            }
            return true;
        } catch (error) {
            console.error('水印初始化失败:', error);
            return false;
        }
    }

    /**
     * 销毁水印
     */
    destroy() {
        // 清理监听器
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        // 清理水印元素
        const existingWatermark = document.getElementById(this.watermarkId);
        if (existingWatermark) {
            existingWatermark.remove();
        }
        this.watermarkElement = null;
    }

    /**
     * 更新水印配置
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
        this.init(); // 重新初始化
    }

    /**
     * 显示水印
     */
    show() {
        if (this.watermarkElement) {
            this.watermarkElement.style.display = 'block';
        }
    }

    /**
     * 隐藏水印
     */
    hide() {
        if (this.watermarkElement) {
            this.watermarkElement.style.display = 'none';
        }
    }

    /**
     * 防篡改监听
     */
    enableProtection() {
        // 如果监听器已存在，不重复创建
        if (this.observer) {
            return this.observer;
        }
        
        if (!window.MutationObserver) {
            console.warn('当前浏览器不支持MutationObserver，无法启用水印防篡改');
            return;
        }

        // 防止过于频繁恢复的标志
        let isRecovering = false;

        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                // 检查是否有节点被移除
                if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
                    for (let node of mutation.removedNodes) {
                        if (node.id === this.watermarkId && !isRecovering) {
                            isRecovering = true;
                            console.log('水印被移除，正在恢复...');
                            
                            setTimeout(() => {
                                // 重新初始化前先清理当前监听器
                                if (this.observer) {
                                    this.observer.disconnect();
                                    this.observer = null;
                                }
                                
                                // 重新初始化水印
                                this.init();
                                isRecovering = false;
                            }, 500);
                            break;
                        }
                    }
                }
                
                // 检查样式是否被修改
                if (mutation.type === 'attributes' && mutation.target.id === this.watermarkId) {
                    if (mutation.target.style.display === 'none') {
                        mutation.target.style.display = 'block';
                    }
                }
            });
        });

        // 监听整个文档
        this.observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });

        return this.observer;
    }
}

// 创建全局水印实例
let globalWatermark = null;

/**
 * 水印工具函数
 */
export const watermarkUtils = {
    /**
     * 初始化全局水印
     */
    init(options = {}) {
        if (!globalWatermark) {
            globalWatermark = new Watermark(options);
        }
        return globalWatermark.init();
    },

    /**
     * 销毁全局水印
     */
    destroy() {
        if (globalWatermark) {
            globalWatermark.destroy();
            globalWatermark = null;
        }
    },

    /**
     * 更新水印配置
     */
    updateConfig(newConfig) {
        if (globalWatermark) {
            globalWatermark.updateConfig(newConfig);
        }
    },

    /**
     * 显示水印
     */
    show() {
        if (globalWatermark) {
            globalWatermark.show();
        }
    },

    /**
     * 隐藏水印
     */
    hide() {
        if (globalWatermark) {
            globalWatermark.hide();
        }
    },

    /**
     * 启用防篡改保护
     */
    enableProtection() {
        if (globalWatermark) {
            return globalWatermark.enableProtection();
        }
    },

    /**
     * 获取水印实例
     */
    getInstance() {
        return globalWatermark;
    }
};

export default Watermark;
